/* ─────────────────────────────────────────────────────────────────
   reveal.js — the site-wide scroll reveal (see reveal.css for the
   convention: one motion, role delays, grids tile by tile).

   • One IntersectionObserver, threshold 0 with the bottom 10% of the screen
     trimmed: an element reveals as its top comes a little way into view, so
     even one taller than the screen always fires. One-shot. Anything already
     scrolled past (a jump link, restored scroll) is shown too.
   • Anything already on screen when this runs stays as it is, never hidden
     (nothing above the fold animates). A section can pre-arm an element by
     adding `.reveal-ready` itself before first paint (the Folio feature, whose
     name sweep starts on load).
   • `[data-reveal-grid]`: each child is its own reveal item. Children arriving
     in the same batch stagger in DOM order, 0.07s apart, at most MAX_STAGGER
     steps, so a long grid never waits on a long chain. Children added later
     (a blog "Load more") or un-hidden later (the Shop's) fade in as they show.
   • `[data-reveal-rail]` (with data-reveal-grid): the rail itself is watched;
     when it comes into view every item reveals, the ones on screen staggered.
   • Once an element has landed, `.reveal-ready` comes off, so it keeps no
     transition overrides or transforms.
   • Theme editor: re-rendered sections are scanned again on
     `shopify:section:load`, so nothing is ever left hidden.
   • No JS, no IntersectionObserver or reduced motion: nothing is touched, so
     everything shows.
   Exposes window.aeReveal.scan(root) for a section's own script.
   ───────────────────────────────────────────────────────────────── */
(function () {
  if (window.aeReveal) return;

  var reduce =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce || !('IntersectionObserver' in window)) return;

  var MAX_STAGGER = 4; // extra steps within one batch of grid items
  var STEP_MS = 70; // keep in step with reveal.css (0.07s)
  var RUN_MS = 500; // the reveal's duration (reveal.css)

  var each = function (list, fn) {
    Array.prototype.forEach.call(list, fn);
  };

  function viewH() {
    return window.innerHeight || document.documentElement.clientHeight;
  }

  // On screen now, vertically. Hidden (display: none) elements have no box.
  function onScreen(el) {
    var r = el.getBoundingClientRect();
    if (!r.width && !r.height) return false;
    return r.bottom > 0 && r.top < viewH();
  }

  function gridOf(el) {
    var p = el.parentElement;
    return p && p.hasAttribute('data-reveal-grid') ? p : null;
  }

  function baseStep(grid) {
    return parseInt(grid.getAttribute('data-reveal-grid'), 10) || 0;
  }

  // Hide an element ready to reveal. Returns false if it should stay as it is.
  function arm(el, keepIfOnScreen) {
    if (el._aeReveal || el.classList.contains('is-revealed')) return false;
    var preArmed = el.classList.contains('reveal-ready');
    if (!preArmed && keepIfOnScreen && onScreen(el)) return false;
    el._aeReveal = true;
    if (!el.hasAttribute('data-reveal')) el.setAttribute('data-reveal', '');
    el.classList.add('reveal-ready');
    return true;
  }

  function reveal(el, step) {
    if (step != null) el.style.setProperty('--reveal-step', step);
    else step = parseInt(el.getAttribute('data-reveal-delay'), 10) || 0;
    el.classList.add('is-revealed');

    // Landed: drop the hidden-state hook (and the inline step).
    var timer;
    function done(e) {
      if (e && (e.target !== el || e.propertyName !== 'opacity')) return;
      el.removeEventListener('transitionend', done);
      clearTimeout(timer);
      el.classList.remove('reveal-ready');
      el.style.removeProperty('--reveal-step');
    }
    el.addEventListener('transitionend', done);
    // Fallback when no transitionend comes (e.g. the element is display: none).
    timer = setTimeout(function () {
      done();
    }, step * STEP_MS + RUN_MS + 300);
  }

  function byDomOrder(a, b) {
    return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  }

  // Rail in view: reveal every armed item; the ones on screen stagger in turn.
  function revealRail(rail) {
    var base = baseStep(rail);
    var vw = window.innerWidth || document.documentElement.clientWidth;
    var i = 0;
    each(rail.children, function (item) {
      if (!item._aeReveal || item.classList.contains('is-revealed')) return;
      var r = item.getBoundingClientRect();
      var seen = (r.width || r.height) && r.right > 0 && r.left < vw;
      reveal(item, seen ? base + Math.min(i++, MAX_STAGGER) : base);
    });
  }

  var io = new IntersectionObserver(
    function (entries) {
      var batches = [];
      entries.forEach(function (entry) {
        var el = entry.target;
        var r = entry.boundingClientRect;
        var passed = r.height > 0 && r.bottom <= 0;
        if (!entry.isIntersecting && !passed) return;
        io.unobserve(el);

        if (el.hasAttribute('data-reveal-rail')) {
          revealRail(el);
          return;
        }
        var grid = gridOf(el);
        if (!grid) {
          reveal(el);
          return;
        }
        for (var b = 0; b < batches.length; b++) {
          if (batches[b].grid === grid) {
            batches[b].items.push(el);
            return;
          }
        }
        batches.push({ grid: grid, items: [el] });
      });

      batches.forEach(function (batch) {
        var base = baseStep(batch.grid);
        batch.items.sort(byDomOrder).forEach(function (el, i) {
          reveal(el, base + Math.min(i, MAX_STAGGER));
        });
      });
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0 }
  );

  function setupGrid(grid) {
    if (grid._aeRevealGrid) return;
    grid._aeRevealGrid = true;

    if (grid.hasAttribute('data-reveal-rail')) {
      if (onScreen(grid)) return; // already in view: leave it as it is
      each(grid.children, function (item) {
        arm(item, false);
      });
      io.observe(grid);
      return;
    }

    each(grid.children, function (item) {
      if (arm(item, true)) io.observe(item);
    });

    // Items added later (a "Load more" that appends) fade in as they show.
    if ('MutationObserver' in window) {
      new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          each(m.addedNodes, function (node) {
            if (node.nodeType === 1 && arm(node, false)) io.observe(node);
          });
        });
      }).observe(grid, { childList: true });
    }
  }

  function scan(root) {
    root = root || document;
    each(root.querySelectorAll('[data-reveal]'), function (el) {
      if (gridOf(el)) return; // a grid item: its grid handles it
      if (arm(el, true)) io.observe(el);
    });
    each(root.querySelectorAll('[data-reveal-grid]'), setupGrid);
  }

  window.aeReveal = { scan: scan };
  scan(document);

  // Theme editor: a section re-renders as new elements; pick them up (anything on
  // screen shows straight away), and stop watching the ones it replaced.
  document.addEventListener('shopify:section:load', function (e) {
    scan(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    each(e.target.querySelectorAll('[data-reveal], [data-reveal-rail]'), function (el) {
      io.unobserve(el);
    });
  });
})();
